import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { useToast } from '../contexts/ToastContext';
import { api } from '../lib/api';

const MAX_TITLE_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 5000;

interface FormErrors {
  title?: string;
  description?: string;
}

export function SubmitPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Title validation
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > MAX_TITLE_LENGTH) {
      newErrors.title = `Title must be ${MAX_TITLE_LENGTH} characters or less`;
    }

    // Description validation
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await api.submitProject({
        title: title.trim(),
        description: description.trim(),
      });

      showToast('Idea submitted successfully!', 'success');
      navigate(`/project/${response.project_id}`);
    } catch (error) {
      console.error('Submit error:', error);
      showToast('Failed to submit idea. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-wolf-100 mb-2">Submit an Idea</h1>
        <p className="text-wolf-300">
          Share your R&D project idea for AI-powered evaluation against our strategic framework.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title Field */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-wolf-300 mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your idea title"
              className={clsx(
                'w-full px-4 py-3 rounded-lg bg-wolf-900/50 border text-wolf-100 placeholder-wolf-500',
                'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent',
                'transition-colors',
                errors.title ? 'border-red-500' : 'border-wolf-700/50'
              )}
              maxLength={MAX_TITLE_LENGTH + 10}
            />
            <div className="flex justify-between mt-1">
              {errors.title ? (
                <span className="text-sm text-red-400">{errors.title}</span>
              ) : (
                <span />
              )}
              <span className={clsx(
                'text-sm',
                title.length > MAX_TITLE_LENGTH ? 'text-red-400' : 'text-wolf-500'
              )}>
                {title.length}/{MAX_TITLE_LENGTH}
              </span>
            </div>
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-wolf-300 mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your idea in detail..."
              rows={6}
              className={clsx(
                'w-full px-4 py-3 rounded-lg bg-wolf-900/50 border text-wolf-100 placeholder-wolf-500',
                'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent',
                'transition-colors resize-none',
                errors.description ? 'border-red-500' : 'border-wolf-700/50'
              )}
              maxLength={MAX_DESCRIPTION_LENGTH + 100}
            />
            <div className="flex justify-between mt-1">
              {errors.description ? (
                <span className="text-sm text-red-400">{errors.description}</span>
              ) : (
                <span />
              )}
              <span className={clsx(
                'text-sm',
                description.length > MAX_DESCRIPTION_LENGTH ? 'text-red-400' : 'text-wolf-500'
              )}>
                {description.length}/{MAX_DESCRIPTION_LENGTH}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 pt-4">
            <Button type="submit" loading={isSubmitting}>
              Submit Idea
            </Button>
            <Link
              to="/dashboard"
              className="text-wolf-300 hover:text-wolf-100 transition-colors"
            >
              Cancel
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
